import React, { useState } from 'react';
import { Modal, Card, Row, Col, Checkbox, Button, Spin, Tag } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  ApartmentOutlined,
  CustomerServiceOutlined,
  CompassOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import './profile-selection-modal.less';

const getAccountTypeIcon = (accountType) => {
  switch (accountType?.toUpperCase()) {
    case 'GROWER':
      return <UserOutlined className="profile-type-icon grower" />;
    case 'SEED_ADVISOR':
      return <TeamOutlined className="profile-type-icon seed-advisor" />;
    case 'SALES_REPRESENTATIVE':
      return <ApartmentOutlined className="profile-type-icon sales-rep" />;
    case 'CUSTOMER_SUPPORT':
      return (
        <CustomerServiceOutlined className="profile-type-icon customer-support" />
      );
    case 'ADMINISTRATOR':
      return <CompassOutlined className="profile-type-icon admin" />;
    default:
      return <UserOutlined className="profile-type-icon" />;
  }
};

const getAccountTypeLabel = (accountType) => {
  switch (accountType?.toUpperCase()) {
    case 'GROWER':
      return 'Grower';
    case 'SEED_ADVISOR':
      return 'Seed Advisor';
    case 'SALES_REPRESENTATIVE':
      return 'Sales Representative';
    case 'ADMINISTRATOR':
      return 'Administrator';
    case 'CUSTOMER_SUPPORT':
      return 'Customer Support';
    default:
      return accountType;
  }
};

const EXTERNAL_PROFILE_TYPES = ['GROWER', 'SEED_ADVISOR'];

const isExternalProfile = (accountType) =>
  EXTERNAL_PROFILE_TYPES.includes(accountType?.toUpperCase());

const ProfileCard = ({
  profile,
  isSelected,
  isDefault,
  onSelect,
  messages,
}) => {
  const {
    firstName,
    lastName,
    accountName,
    accountType,
    billingAddress,
    fsapId,
  } = profile;
  const showExternalFields = isExternalProfile(accountType);

  return (
    <Card
      className={`profile-card ${isSelected ? 'selected' : ''}`}
      onClick={() => onSelect(profile)}
      hoverable
    >
      <div className="profile-card-content">
        <div className="profile-icon-container">
          {getAccountTypeIcon(accountType)}
        </div>
        <div className="profile-details">
          <h3 className="profile-name">
            {firstName} {lastName}
            {isDefault && (
              <Tag color="blue" className="default-profile-tag">
                Default
              </Tag>
            )}
          </h3>
          {showExternalFields && accountName && (
            <div className="profile-info account-name">
              <span className="profile-label">{messages.account_name}:</span>
              <span className="profile-value">{accountName}</span>
            </div>
          )}
          {showExternalFields && (
            <div className="profile-info">
              <span className="profile-label">{messages.fsap_id}:</span>
              <span className="profile-value">{fsapId || '—'}</span>
            </div>
          )}
          <div className="profile-info">
            <span className="profile-label">{messages.account_type}:</span>
            <span className="profile-value">
              {getAccountTypeLabel(accountType)}
            </span>
          </div>
          {showExternalFields && billingAddress && (
            <div className="profile-info">
              <span className="profile-label">{messages.address}:</span>
              <span className="profile-value">{billingAddress}</span>
            </div>
          )}
        </div>
        {isSelected && (
          <div className="profile-selected-indicator">
            <span className="checkmark">✓</span>
          </div>
        )}
      </div>
    </Card>
  );
};

ProfileCard.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    accountContactId: PropTypes.string,
    accountId: PropTypes.string,
    fsapId: PropTypes.string,
    salesForceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    toolAdmin: PropTypes.bool,
    accountName: PropTypes.string,
    accountType: PropTypes.string.isRequired,
    billingAddress: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  isDefault: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  messages: PropTypes.object.isRequired,
};

const ProfileSelectionModal = ({
  visible,
  profiles,
  loading,
  onSelectProfile,
  onCancel,
  defaultProfileId,
  messages,
}) => {
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [setAsDefault, setSetAsDefault] = useState(false);

  const resetModalState = () => {
    setSelectedProfile(null);
    setSetAsDefault(false);
  };

  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
  };

  const handleConfirm = () => {
    if (selectedProfile) {
      onSelectProfile(selectedProfile, setAsDefault);
      resetModalState();
    }
  };

  const handleCancel = () => {
    resetModalState();
    if (onCancel) {
      onCancel();
    }
  };

  const handleDefaultChange = (e) => {
    setSetAsDefault(e.target.checked);
  };

  const showCloseButton = typeof onCancel === 'function';

  return (
    <Modal
      visible={visible}
      title={messages.select_profile_title}
      centered
      closable={showCloseButton}
      maskClosable={showCloseButton}
      onCancel={showCloseButton ? handleCancel : undefined}
      className="profile-selection-modal"
      width={700}
      footer={[
        <div key="footer" className="profile-modal-footer">
          <Checkbox
            checked={setAsDefault}
            onChange={handleDefaultChange}
            disabled={!selectedProfile}
          >
            {messages.set_as_default}
          </Checkbox>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={!selectedProfile}
            className="confirm-button"
          >
            {messages.continue_button}
          </Button>
        </div>,
      ]}
    >
      {loading ? (
        <div className="profile-loading">
          <Spin size="large" />
          <p>{messages.loading_profiles}</p>
        </div>
      ) : (
        <>
          <p className="profile-selection-description">
            {messages.select_profile_description}
          </p>
          <Row gutter={[16, 16]} className="profiles-container">
            {profiles.map((profile) => (
              <Col xs={24} sm={24} md={12} key={profile.id}>
                <ProfileCard
                  profile={profile}
                  isSelected={selectedProfile?.id === profile.id}
                  isDefault={
                    defaultProfileId != null &&
                    String(profile.id) === String(defaultProfileId)
                  }
                  onSelect={handleProfileSelect}
                  messages={messages}
                />
              </Col>
            ))}
          </Row>
        </>
      )}
    </Modal>
  );
};

ProfileSelectionModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      accountContactId: PropTypes.string,
      accountId: PropTypes.string,
      fsapId: PropTypes.string,
      salesForceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      toolAdmin: PropTypes.bool,
      accountName: PropTypes.string,
      accountType: PropTypes.string.isRequired,
      billingAddress: PropTypes.string,
    })
  ).isRequired,
  loading: PropTypes.bool,
  onSelectProfile: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  defaultProfileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  messages: PropTypes.object.isRequired,
};

ProfileSelectionModal.defaultProps = {
  loading: false,
  onCancel: () => {},
};

export default ProfileSelectionModal;
